import { __decorate } from "tslib";
import { html } from 'lit';
import { CipublicElement } from '@cigna/cigna_csg_gsg_cipublic-shared/lib/mixins/base.js';
import { property, state } from 'lit/decorators.js';
import { emit } from '@cigna/cigna_csg_gsg_cipublic-shared/lib/utils/event.js';
import { InternalI18n } from '@cigna/cigna_csg_gsg_cipublic-shared/lib/i18n/internal-i18n-controller.js';
import { locales } from './locales.js';
import styles from './progress-bar.scss.js';
import '@cigna/cigna_csg_gsg_cipublic-icon/cipublic-icon.js';
/**
 * @element cipublic-progress-bar
 *
 * TODO:
 * - translations
 *
 * @slot heading - `initial` heading
 * @slot label - `initial` label
 * @slot success - `success` heading
 */
export class ProgressBar extends CipublicElement {
    constructor() {
        super(...arguments);
        this.i18n = new InternalI18n(this, locales);
        /**
         * The background color.
         */
        this.background = 'primary';
        /**
         * Value representing the number of decimal places to 'precision'
         */
        this.precision = 0;
        /**
         * The current step.
         */
        this.value = 0;
        /**
         * progress bar type
         */
        this.variant = 'percentage';
        this.emitConnectedCallback = false;
    }
    static get styles() {
        return [styles];
    }
    /**
     * clamp value between 0 and max
     */
    get clampedValue() {
        return Math.min(Math.max(this.value, 0), this.max);
    }
    /**
     * Language-sensitive number formatting
     *
     * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat
     */
    get numberFormatAPI() {
        return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: this.precision,
            maximumFractionDigits: this.precision,
        });
    }
    get state() {
        if (this.clampedValue === 0) {
            return 'initial';
        }
        if ((this.clampedValue === this.max) && this.completeOnLastStep !== true) {
            return 'complete';
        }
        if (this.errorState) {
            return 'error';
        }
        return 'in-progress';
    }
    get percentage() {
        if (!this.max) {
            return 0;
        }
        const getPercentageValue = (this.clampedValue / this.max) * 100;
        return getPercentageValue % 1 === 0 ? Math.round(getPercentageValue) : this.numberFormatAPI.format(getPercentageValue);
    }
    connectedCallback() {
        super.connectedCallback();
        if (this.emitConnectedCallback) {
            emit(this, 'connected-callback');
        }
    }
    renderPrefix() {
        if (this.variant === 'percentage') {
            return html `<p class="prefix">${this.i18n.t('progressbar_progress')}</p>`;
        }
        return html `<p class="prefix">${this.i18n.t('step')} ${this.clampedValue} ${this.i18n.t('of')} ${this.max}</p>`;
    }
    renderSuffix() {
        if (this.variant === 'percentage') {
            return html `<p class="suffix">${this.percentage}%<span> ${this.i18n.t('progressbar_complete')}</span></p>`;
        }
        return html ``;
    }
    render() {
        const showStatus = this.state === 'initial' || this.state === 'in-progress' || (this.state === 'error' && this.errorState === 'stalled');
        let statusHtml;
        if (showStatus) {
            if (this.state === 'initial') {
                statusHtml = html `<slot name="label"></slot>`;
            }
            else {
                statusHtml = html `${this.renderPrefix()}<div class="progress"><div class="progress-bar" style="--progress-bar-value:${this.percentage}"></div></div>${this.renderSuffix()}`;
            }
        }
        return html `${this.state === 'complete' ? '' : html `<slot name="heading"></slot>`} ${showStatus ? html `<div class="progress-status">${statusHtml}</div>` : ''} ${this.state === 'complete'
            ? html `<cipublic-icon set="system" name="check-circle"></cipublic-icon><slot name="success"></slot>`
            : ''}`;
    }
}
__decorate([
    state()
], ProgressBar.prototype, "clampedValue", null);
__decorate([
    property({ attribute: 'status' })
], ProgressBar.prototype, "status", void 0);
__decorate([
    property({ attribute: 'error-state' })
], ProgressBar.prototype, "errorState", void 0);
__decorate([
    property({ reflect: true })
], ProgressBar.prototype, "background", void 0);
__decorate([
    property({ attribute: 'bar-color' })
], ProgressBar.prototype, "barColor", void 0);
__decorate([
    property({ reflect: true, type: Number })
], ProgressBar.prototype, "max", void 0);
__decorate([
    property({ reflect: true, type: Boolean })
], ProgressBar.prototype, "completeOnLastStep", void 0);
__decorate([
    state()
], ProgressBar.prototype, "numberFormatAPI", null);
__decorate([
    property({ type: Number, reflect: true })
], ProgressBar.prototype, "precision", void 0);
__decorate([
    property({ reflect: true, type: Number })
], ProgressBar.prototype, "value", void 0);
__decorate([
    property({ type: String, reflect: true })
], ProgressBar.prototype, "variant", void 0);
__decorate([
    property({ reflect: true, type: Boolean })
], ProgressBar.prototype, "emitConnectedCallback", void 0);
//# sourceMappingURL=progress-bar.js.map